import React from 'react';
import { useLocation, useNavigate, Outlet, Link } from 'react-router-dom';
import {
    LayoutDashboard, Users, Settings, FileCode,
    Palette, BookOpen, FileText, Search, Video, Mail, List, Sparkles, Brain
} from 'lucide-react';
import '../pages/admin/AdminPanel.css';

const AdminLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Map URL paths to active panel identifiers
    const getActivePanel = (pathname) => {
        if (pathname.includes('/admin/dashboard')) return 'dashboard';
        if (pathname.includes('/admin/blog')) return 'blog';
        if (pathname.includes('/admin/seo')) return 'seo';
        if (pathname.includes('/admin/news')) return 'news';
        if (pathname.includes('/admin/authors')) return 'authors';
        if (pathname.includes('/admin/glossary')) return 'glossary';
        if (pathname.includes('/admin/webinars')) return 'webinars';
        if (pathname.includes('/admin/resources')) return 'resources';
        if (pathname.includes('/admin/sitemaps')) return 'sitemaps';
        if (pathname.includes('/admin/settings')) return 'settings';
        if (pathname.includes('/admin/users')) return 'users';
        if (pathname.includes('/admin/email-server')) return 'email-server';
        if (pathname.includes('/admin/menus')) return 'menus';
        if (pathname.includes('/admin/scripts')) return 'scripts';
        if (pathname.includes('/admin/design')) return 'design';
        if (pathname.includes('/admin/themes')) return 'themes';
        if (pathname.includes('/admin/ai-hub')) return 'ai-hub';
        if (pathname.includes('/admin/ai-settings')) return 'ai-settings';
        return 'dashboard';
    };

    const activePanel = getActivePanel(location.pathname);

    const handleNav = (panel, path) => {
        if (path === '/admin/dashboard') {
            navigate(path, { state: { activePanel: panel } });
        } else {
            navigate(path);
        }
    };

    return (
        <div className="admin-container">
            {/* ── Sidebar ── */}
            <aside className="sidebar">
                <div className="sidebar-logo">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-[#A2CB52] rounded-lg flex items-center justify-center">
                            <span className="text-[#20222D] font-bold text-lg">B</span>
                        </div>
                        <div className="logo-tag" style={{ margin: 0 }}>Admin Panel</div>
                    </div>
                    <Link to="/" className="no-underline hover:opacity-80">
                        <h2 style={{ fontSize: '18px', color: 'var(--text)' }}>
                            BPO<span style={{ color: 'var(--accent)' }}>Consultants</span>
                        </h2>
                    </Link>
                </div>
                <nav className="sidebar-nav">
                    <div className="nav-section-label">Main</div>
                    <div className={`nav-item ${activePanel === 'dashboard' ? 'active' : ''}`} onClick={() => handleNav('dashboard', '/admin/dashboard')}>
                        <LayoutDashboard size={14} /> Dashboard
                    </div>
                    {/* Placeholder for leads count if we want to add it back later via context/store */}
                    <div className={`nav-item ${activePanel === 'seo' ? 'active' : ''}`} onClick={() => handleNav('seo', '/admin/seo')}>
                        <Search size={14} /> SEO Manager
                    </div>
                    <div className={`nav-item ${activePanel === 'ai-hub' ? 'active' : ''}`} onClick={() => handleNav('ai-hub', '/admin/ai-hub')}>
                        <Sparkles size={14} /> AI Intelligence Hub
                    </div>

                    <div className="nav-section-label">Admin</div>
                    <div className={`nav-item ${activePanel === 'users' ? 'active' : ''}`} onClick={() => handleNav('users', '/admin/dashboard')}>
                        <Users size={14} /> Admin Users
                    </div>
                    <div className={`nav-item ${activePanel === 'settings' ? 'active' : ''}`} onClick={() => handleNav('settings', '/admin/dashboard')}>
                        <Settings size={14} /> Settings
                    </div>
                    <div className={`nav-item ${activePanel === 'ai-settings' ? 'active' : ''}`} onClick={() => handleNav('ai-settings', '/admin/ai-settings')}>
                        <Brain size={14} /> AI Settings
                    </div>
                    <div className={`nav-item ${activePanel === 'sitemaps' ? 'active' : ''}`} onClick={() => handleNav('sitemaps', '/admin/sitemaps')}>
                        <Search size={14} /> Sitemap Manager
                    </div>
                    <div className={`nav-item ${activePanel === 'email-server' ? 'active' : ''}`} onClick={() => handleNav('email-server', '/admin/dashboard')}>
                        <Mail size={14} /> Email Server
                    </div>
                    <div className={`nav-item ${activePanel === 'menus' ? 'active' : ''}`} onClick={() => handleNav('menus', '/admin/menus')}>
                        <List size={14} /> Menu Manager
                    </div>
                    <div className={`nav-item ${activePanel === 'scripts' ? 'active' : ''}`} onClick={() => handleNav('scripts', '/admin/dashboard')}>
                        <FileCode size={14} /> Scripts & Tags
                    </div>
                    <div className={`nav-item ${activePanel === 'design' ? 'active' : ''}`} onClick={() => handleNav('design', '/admin/design')}>
                        <Palette size={14} /> Design & Theme
                    </div>

                    <div className="nav-section-label">Content</div>
                    <div className={`nav-item ${activePanel === 'news' ? 'active' : ''}`} onClick={() => handleNav('news', '/admin/news')}>
                        <FileText size={14} /> News Manager
                    </div>
                    <div className={`nav-item ${activePanel === 'webinars' ? 'active' : ''}`} onClick={() => handleNav('webinars', '/admin/webinars')}>
                        <Video size={14} /> Webinars & Events
                    </div>
                    <div className={`nav-item ${activePanel === 'resources' ? 'active' : ''}`} onClick={() => handleNav('resources', '/admin/resources')}>
                        <BookOpen size={14} /> Resources
                    </div>
                    <div className={`nav-item ${activePanel === 'blog' ? 'active' : ''}`} onClick={() => handleNav('blog', '/admin/blog')}>
                        <FileText size={14} /> Blog Posts
                    </div>
                    <div className={`nav-item ${activePanel === 'authors' ? 'active' : ''}`} onClick={() => handleNav('authors', '/admin/authors')}>
                        <Users size={14} /> Authors
                    </div>
                    <div className={`nav-item ${activePanel === 'glossary' ? 'active' : ''}`} onClick={() => handleNav('glossary', '/admin/glossary')}>
                        <BookOpen size={14} /> Glossary
                    </div>
                </nav>
                <div className="sidebar-footer">
                    <div className="user-chip">
                        <div className="user-avatar">SA</div>
                        <div className="user-info">
                            <div className="name">Super Admin</div>
                            <div className="role">superadmin</div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* ── Main Content ── */}
            <main className="main">
                <div className="topbar">
                    <div className="topbar-title">
                        {activePanel.charAt(0).toUpperCase() + activePanel.slice(1).replace('-', ' ')}
                    </div>
                    <div className="topbar-actions">
                        <span style={{ fontSize: '10px', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }}></span>
                            Internal DB Active
                        </span>
                    </div>
                </div>

                <div className="content">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
